<template>
  <div class="mt-6">
    <loader v-if="loading" />

    <panel class="pt-6" v-else-if="subscription_count === 0">
      <div>
        <i class="fas fa-info-circle"></i> You'll be able to manage your data here after you've enabled your first Auto Prospecting subscription.
      </div>
    </panel>

    <div class="bg-red-dark text-white mb-6 p-3" v-else-if="locked && !canCreateAutomatedPrintOrders">
      <i class="fa fa-exclamation-triangle"></i> Automated print order processing is currently disabled for your office. Please contact us at <a :href="supportLink" class="text-white">{{ system.support_email }}</a> to re-enable your services.
    </div>

    <panel class="pt-6" v-else-if="isApsHoliday">
      <div>
        <i class="fas fa-info-circle"></i> Auto Prospecting is currently in holiday mode and will return soon.
      </div>
    </panel>

    <panel class="pt-6" v-else-if="locked">
      <div>
        <i class="fas fa-info-circle"></i> Your orders are now being processed.
      </div>
    </panel>

    <blacklist v-else-if="displayBlacklist" @hide="hideBlacklist()" :key="blacklistKey" />

    <div v-else>
      <hr class="mt-2 border-bottom w-full">
      <div class="w-full h-screen">
        <panel class="pt-4 mb-3">
          <div class="flex w-full py-5">
            <h2 class="w-1/4 text-xl">Current data at a glance: </h2>
            <div class="flex-column-reverse w-3/4 text-right text-lg flex flex-wrap items-center">
              <post-codes-covered />
            </div>
          </div>
        </panel>
        <panel class="pt-4 mb-3">
          <div class="flex flex-col lg:flex-row w-full">
            <div class="flex flex-col w-full lg:w-1/5 text-center items-center py-4 px-6 lg:border-r">
              <p class="uppercase">Active properties in this run</p>
              <p class="font-bold text-blue text-2xl pb-2 mb-4 item-middle py-5">{{ total | number }}</p>
              <p class="text-xs">* matching your active triggers</p>
            </div>
            <div class="flex flex-col w-full lg:w-1/5 uppercase items-center py-4 px-6 lg:border-r">
              <div class="items-left w-full">
                <p class="uppercase pb-2">Active Triggers</p>
                <p class="uppercase font-bold py-2 w-full">New to Market <span class="text-blue float-right text-right">{{ newToMarketCount | number }}</span></p>
                <p class="uppercase font-bold py-2 w-full">Price Reduction <span class="text-blue float-right text-right">{{ priceReductionCount | number }}</span></p>
                <p class="uppercase font-bold pt-2 w-full">Fallen Through <span class="text-blue float-right text-right">{{ fallenThroughCount | number }}</span></p>
              </div>
            </div>
            <div class="flex flex-col w-full lg:w-1/5 uppercase justify-center lg:py-4 pb-4 px-6 lg:border-r">
              <div class="items-left w-full">
                <p class="uppercase font-bold py-2 w-full">Withdrawal <span class="text-blue float-right text-right"> {{ withdrawCount | number }}</span></p>
                <p class="uppercase font-bold pt-2 w-full">Unsold (All) <span class="text-blue float-right text-right">{{ unsoldCount | number }}</span></p>
              </div>
            </div>
            <div class="flex flex-col w-full lg:w-1/5 uppercase items-center py-4 px-6 lg:border-r">
              <div class="items-left w-full">
                <p class="uppercase pb-2">Excluded Properties</p>
                <p class="uppercase font-bold py-2 w-full">Blacklisted <span class="text-red float-right text-right">{{ blacklistedCount | number }}</span></p>
                <p class="uppercase font-bold pt-2 w-full">Contacted <span class="text-red float-right text-right">{{ contactedCount | number }}</span></p>
                <p class="text-xs">within the last {{ prospectingDelay }} days</p>
                <p class="uppercase font-bold py-2 w-full">Excluded <span class="text-red float-right text-right">{{ excludedCount | number }}</span></p>
              </div>
            </div>
            <div class="flex flex-col w-full lg:w-1/5 text-center items-center py-4 px-6">
              <p class="uppercase">Number of properties to prospect to</p>
              <p class="font-bold text-green text-2xl pb-2 mb-4 item-middle py-5">{{ activeTotal | number }}</p>
              <div v-if="nextPrintCollection">
                <p class="text-sm pb-1">Scheduled to print</p>
                <p class="text-lg text-green pb-1">{{ nextPrintCollection }}</p>
                <p class="text-xs">6pm (18:00)</p>
              </div>
            </div>
          </div>
        </panel>
        <panel class="pt-6" v-if="locked">
          <div>
            <i class="fas fa-info-circle"></i> Your on-market orders are now being processed.
          </div>
        </panel>

        <div class="bg-red-dark text-white mb-6 p-3" v-if="!canCreateAutomatedPrintOrders">
          <i class="fa fa-exclamation-triangle"></i> Automated print order processing is currently disabled for your office. Please contact us at <a :href="supportLink" class="text-white">{{ system.support_email }}</a> to re-enable your services.
        </div>

        <div class="flex w-full items-center">
          <div class="w-3/4 mr-4">
            <div class="flex flex-wrap md:flex-row align-middle items-center py-5 mb-5">
              <div class="bg-white border md:w-1/2 w-full mb-6 md:mb-0 px-3 flex items-center rounded overflow-hidden">
                <input v-model="filters.text" type="text" class="py-4 px-2 w-full" placeholder="Search Postcode / Price" />
                <button @click="filters.text='';getProperties(true)">
              <span class="w-auto flex justify-end text-grey p-1">
                <i class="fa fa-times text-grey"></i>
              </span>
                </button>
                <button @click="getProperties(true)">
              <span class="w-auto flex justify-end text-grey p-1">
                <i class="fa fa-search text-grey"></i>
              </span>
                </button>
              </div>
            </div> <!-- end of search / stats header -->

            <div class="flex flex-col lg:flex-row mb-6"> <!-- start of filters -->
              <div class="flex-col mb-4 mr-4 lg:mr-2">
                <label for="department" class="mb-2 text-sm uppercase">Department</label>
                <pl-select v-model="filters.department" name="filter_menu" id="department" select-class="bg-white" class="flex-grow lg:w-40 w-full" @change="getProperties(true)">
                  <option value="SALES-LET">Sales &amp; Lettings</option>
                  <option value="SALES">Sales</option>
                  <option value="LET">Lettings</option>
                </pl-select>
              </div>
              <div class="flex-col mb-4 mr-4 lg:mr-2">
                <label for="include" class="mb-2 text-sm uppercase">Included/Excluded</label>
                <pl-select v-model="filters.included" name="filter_menu" id="include" select-class="bg-white" class="flex-grow lg:w-40 w-full" @change="getProperties(true)">
                  <option value="all">All</option>
                  <option value="included">Included</option>
                  <option value="excluded">Not Included</option>
                </pl-select>
              </div>
              <div class="flex-col mb-4 mr-4 lg:mr-2" v-if="selectedOffice.network_id == 7">
                <label for="data_only" class="mb-2 text-sm uppercase">Print/Data</label>
                <pl-select v-model="filters.data_only" name="filter_menu" id="data_only" select-class="bg-white" class="flex-grow lg:w-40 w-full" @change="getProperties(true)">
                  <option value="all">Both</option>
                  <option :value="0">Print</option>
                  <option :value="1">Data Only</option>
                </pl-select>
              </div>
            </div>
            <div class="flex flex-col lg:flex-row mb-6"> <!-- start of filters -->
              <div class="flex-col mb-4 lg:mr-2">
                <label for="trigger" class="mb-2 text-sm uppercase">Trigger</label>
                <pl-select v-model="filters.trigger" name="filter_menu" id="trigger" select-class="bg-white" class="flex-grow lg:w-40 w-full" @change="getProperties(true)">
                  <option value="all">All</option>
                  <option :value="4">New to Market</option>
                  <option :value="5">Price Reduction</option>
                  <option :value="6">Fallen Through</option>
                  <option :value="7">Withdrawal</option>
                  <option :value="17">Unsold (1 Week)</option>
                  <option :value="18">Unsold (2 Weeks)</option>
                  <option :value="13">Unsold (3 Weeks)</option>
                  <option :value="8">Unsold (4 Weeks)</option>
                  <option :value="19">Unsold (5 Weeks)</option>
                  <option :value="20">Unsold (6 Weeks)</option>
                  <option :value="14">Unsold (7 Weeks)</option>
                  <option :value="9">Unsold (8 Weeks)</option>
                  <option :value="21">Unsold (9 Weeks)</option>
                  <option :value="22">Unsold (10 Weeks)</option>
                  <option :value="15">Unsold (11 Weeks)</option>
                  <option :value="10">Unsold (12 Weeks)</option>
                  <option :value="23">Unsold (13 Weeks)</option>
                  <option :value="24">Unsold (14 Weeks)</option>
                  <option :value="16">Unsold (15 Weeks)</option>
                  <option :value="11">Unsold (16 Weeks)</option>
                  <option :value="25">Unsold (17 Weeks)</option>
                  <option :value="26">Unsold (18 Weeks)</option>
                  <option :value="27">Unsold (19 Weeks)</option>
                  <option :value="28">Unsold (20 Weeks)</option>
                  <option :value="29">Unsold (21 Weeks)</option>
                  <option :value="30">Unsold (22 Weeks)</option>
                  <option :value="31">Unsold (23 Weeks)</option>
                  <option :value="32">Unsold (24 Weeks)</option>
                  <option :value="33">Unsold (25 Weeks)</option>
                  <option :value="12">Unsold (26 Weeks)</option>
                  <option :value="34">Unsold (27 Weeks)</option>
                  <option :value="35">Unsold (28 Weeks)</option>
                </pl-select>
              </div>
              <div class="flex-col mb-4 lg:mr-2">
                <label for="trigger_date" class="mb-2 text-sm uppercase">Trigger Date</label>
                <pl-select v-model="filters.trigger_date" name="filter_menu" id="trigger_date" select-class="bg-white" class="flex-grow lg:w-40 w-full" @change="getProperties(true)">
                  <option value="all">All</option>
                  <option v-for="date in triggerDateFilter" :key="date" :value="date">{{ date | date(false) }}</option>
                </pl-select>
              </div>
              <div class="flex-col mb-4 lg:mr-2">
                <label for="min_price" class="mb-2 text-sm uppercase">Min Price</label>
                <pl-select v-model="filters.min_price" name="min_price" id="min_price" select-class="bg-white" class="flex-grow lg:w-40 w-full" @change="getProperties(true)">
                  <option :value="0">No Minimum</option>
                  <option v-for="n in salesPriceRange" :key="n" :value="n" v-if="filters.max_price === 0 || filters.max_price > n">
                    £{{ n | number }}
                  </option>
                </pl-select>
              </div>
              <div class="flex-col mb-4 lg:mr-2">
                <label for="min_price" class="mb-2 text-sm uppercase">Max Price</label>
                <pl-select v-model="filters.max_price" name="max_price" id="max_price" select-class="bg-white" class="flex-grow lg:w-40 w-full" @change="getProperties(true)">
                  <option :value="0">No Maximum</option>
                  <option v-for="n in salesPriceRange" :key="n" :value="n" v-if="filters.min_price === 0 || filters.min_price < n">
                    £{{ n | number }}
                  </option>
                </pl-select>
              </div>
              <div class="flex-col mb-4 lg:mr-2">
                <label for="agent" class="mb-2 text-sm uppercase">Agent</label>
                <pl-select v-model="filters.agent" name="filter_menu" id="agent" select-class="bg-white" class="flex-grow lg:w-40 w-full" @change="getProperties(true)">
                  <option value="all">All</option>
                  <option v-for="agent in agentFilter" :key="agent" :value="agent">{{ agent }}</option>
                </pl-select>
              </div>
            </div> <!-- end of filters -->
          </div>
          <div class="flex-col md:w-1/4 w-full my-2">
            <div class="rounded-lg bg-white p-8">
              <blacklist :summaryView="true" @show="displayBlacklist = true" :key="blacklistKey" />
              <div v-if="selectedOffice.network_id === 7" class="flex py-2 justify-center w-full">
                <div class="text-center">
                  <div v-if="dataOnlyCount !== null" class="my-2"><span class="font-black">{{ dataOnlyCount }}</span> {{ dataOnlyCount === 1 ? 'Property' : 'Properties' }} in 'Data Only'</div>
                  <button @click="downloadProperty()" class="bg-blue hover:bg-primary text-white py-3 px-8 text-sm uppercase rounded shadow flex">Download Addresses
                    <icon name="file-arrow-down-thin-white" :center="false" class="w-3 ml-2" /></button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="w-full flex justify-around text-sm md:hidden">
          <i class="fa fa-arrow-left"></i> Scroll the table left &amp; right to see more <i class="fa fa-arrow-right"></i>
        </div>
        <panel class="pt-6">
          <div v-if="propertiesTable.length === 0">
            <i class="fas fa-info-circle"></i> Your search returned no results.
          </div>

          <div class="w-full" v-else>
            <div class="flex flex-row items-center pb-4 border-bottom mb-4">
              <div class="w-full md:flex-shrink">
                <div class="font-bold text-lg">All properties in this print run: </div>
              </div>
              <div class="w-full md:flex-grow text-right">
                <div class="text-sm text-grey-dark mb-1">Results:</div>
                <div class="mb-2"><span class="text-green ml-2">{{ this.filteredCount | number }} Filtered Properties</span> / {{ this.total | number }} Total Properties</div>
              </div>
            </div>
            <table class="w-full">
              <thead class="whitespace-no-wrap">
              <tr class="text-xs align-top">
                <th class="py-5"  @click="sortBy('department')">DEPARTMENT
                  <i v-if="sorting['department']==='asc'" class="fa fa-sort-down cursor-pointer"></i>
                  <i v-else-if="sorting['department']==='desc'" class="fa fa-sort-up cursor-pointer"></i>
                  <i v-else class="fa fa-sort cursor-pointer"></i>
                </th>
                <th class="py-5" @click="sortBy('address')">PROPERTY
                  <i v-if="sorting['address']==='asc'" class="fa fa-sort-down cursor-pointer"></i>
                  <i v-else-if="sorting['address']==='desc'" class="fa fa-sort-up cursor-pointer"></i>
                  <i v-else class="fa fa-sort cursor-pointer"></i>
                </th>
                <th class="py-5" @click="sortBy('price')">PRICE
                  <i v-if="sorting['price']==='asc'" class="fa fa-sort-down cursor-pointer"></i>
                  <i v-else-if="sorting['price']==='desc'" class="fa fa-sort-up cursor-pointer"></i>
                  <i v-else class="fa fa-sort cursor-pointer"></i>
                </th>
                <th class="py-5" @click="sortBy('trigger')">TRIGGER
                  <i v-if="sorting['trigger']==='asc'" class="fa fa-sort-down cursor-pointer"></i>
                  <i v-else-if="sorting['trigger']==='desc'" class="fa fa-sort-up cursor-pointer"></i>
                  <i v-else class="fa fa-sort cursor-pointer"></i>
                </th>
                <th class="py-5" @click="sortBy('trigger_date')">TRIGGER DATE
                  <i v-if="sorting['trigger_date']==='asc'" class="fa fa-sort-down cursor-pointer"></i>
                  <i v-else-if="sorting['trigger_date']==='desc'" class="fa fa-sort-up cursor-pointer"></i>
                  <i v-else class="fa fa-sort cursor-pointer"></i>
                </th>
                <th class="py-5 pr-2" @click="sortBy('agent')">LISTING AGENT
                  <i v-if="sorting['agent']==='asc'" class="fa fa-sort-down cursor-pointer"></i>
                  <i v-else-if="sorting['agent']==='desc'" class="fa fa-sort-up cursor-pointer"></i>
                  <i v-else class="fa fa-sort cursor-pointer"></i>
                </th>
                <th class="py-5 pr-2" @click="sortBy('last_contacted')">LAST CONTACTED
                  <i v-if="sorting['last_contacted']==='asc'" class="fa fa-sort-down cursor-pointer"></i>
                  <i v-else-if="sorting['last_contacted']==='desc'" class="fa fa-sort-up cursor-pointer"></i>
                  <i v-else class="fa fa-sort cursor-pointer"></i>
                </th>
                <th class="py-5 pr-5 relative">
                  BLACKLIST
                  <div class="text-blue inline hover-trigger">
                    <i class="fas fa-info-circle"></i>
                    <div class="hover-content bg-white border border-tertiary p-3 text-xs shadow text-left text-black font-normal whitespace-normal">
                      Clicking the icon will permanently remove the property from this mail out and any future mail outs by adding it to a property blacklist.
                    </div>
                  </div>
                </th>
                <th class="py-5 relative">
                  <include-popup  @include-all="includeAll"></include-popup>
                </th>
              </tr>
              </thead>

              <tbody>
              <tr v-for="property in propertiesTable" :key="property.id" class="bg-grey-lighter text-xs md:text-sm border-b text-center border-l-4 border-r-4" :class="{'bg-green-lightest border-green': property.include && ! property.date_restricted && !property.data_only}">
                <td class="p-5 font-bold whitespace-no-wrap">
                  &nbsp;{{ property.department }}</td>
                <td class="p-5"><a :href="property.url" class="text-primary" target="_blank">{{ property.full_address }}</a></td>
                <td class="p-5" :class="property.on_market?'font-bold':'italic'">{{property.priceFormatted}}</td>
                <td class="p-5">{{ property.triggerText }}</td>
                <td class="p-5">{{ property.triggerDate | date(false) }}<br />{{ property.triggerDate | dayShortName(false) }}</td>
                <td class="p-5">
                  <div class="mb-1">{{ property.on_market ? property.agent : 'N/A' }}</div>
                  <a @click="showExcludeAgentModal(property.agent)" class="text-xs text-green-dark underline cursor-pointer">Exclude agent</a>
                </td>
                <td class="p-5"><last-contacted-popup :property="property" :officeId="selectedOffice.id" /></td>
                <td class="p-5 cursor-pointer text-lg" @click="changeBlacklist(property)" title="Blacklist" :class="property.blacklist ? 'text-red-dark hover:text-red-darker' : 'text-grey hover:text-grey-darker'">
                  <i class="fas fa-times-circle"></i>
                </td>
                <td class="p-5 flex justify-center items-center" title="Include">
                  <div v-if="property.date_restricted" class="flex justify-center">
                    <div v-if="!property.data_only ||  selectedOffice.network_id !== 7">
                      Contacted: {{ property.last_contacted }}
                    </div>
                    <div v-else-if="selectedOffice.network_id === 7" @click="downloadProperty(property)" class="flex items-center justify-center w-full">
                      <icon name="file-csv-light-blue" class="w-5 mt-1 cursor-pointer" />
                    </div>
                  </div>
                  <div v-else-if="!property.data_only ||  selectedOffice.network_id !== 7" class="flex items-center justify-center w-full">
                    <input type="checkbox" class="green" :id="`include${property.id}`" v-model="property.include" @change="changeInclude(property)" :disabled="property.blacklist">
                    <label :for="`include${property.id}`"></label>
                  </div>
                  <div v-else-if="selectedOffice.network_id === 7" @click="downloadProperty(property)" class="flex items-center justify-center w-full">
                    <icon name="file-csv-light-blue" class="w-5 mt-1 cursor-pointer" />
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
            <div class="text-center mt-4">
              <paginator :data="meta" @change="getPaginatedProperties"/>
            </div>
          </div>
          <modal
            width="70vw"
            v-if="showExcludeModal !== false"
            @leave="showExcludeModal = false; excludeAgentName = null"
            show-close-btn
          >
            <p class="text-sm mb-4">Are you sure you would like to exclude {{ excludeAgentName }} properties from print?</p>
            <p class="text-sm mb-4">{{ excludeAgentName }} properties will be excluded from this mail out and from future mail outs.</p>
            <button @click="excludeAgent(excludeAgentName)"
                class="text-white text-xs rounded flex items-center justify-center px-12 h-10 bg-black hover:bg-grey-dark">
              <span>Confirm</span>
            </button>
          </modal>
        </panel>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/http'
import { mapState, mapActions } from 'vuex'
import Blacklist from '@/components/AutoProspecting/Blacklist'
import IncludePopup from '@/components/AutoProspecting/IncludePopup'
import LastContactedPopup from '@/components/AutoProspecting/LastContactedPopup'
import Paginator from '../../components/Paginator.vue'
import PostCodesCovered from '@/components/AutoProspecting/PostCodesCovered.vue'

export default {
  name: 'apsTodayDataPage',
  components: {
    Blacklist,
    IncludePopup,
    LastContactedPopup,
    Paginator,
    PostCodesCovered
  },
  data () {
    return {
      locked: false,
      downloadingData: false,
      subscription_count: 0,
      properties: {},
      loading: true,
      displayBlacklist: false,
      blacklistKey: 0,
      agentFilter: [],
      triggerDateFilter: [],
      filters: {
        department: 'SALES-LET',
        included: 'all',
        data_only: 'all',
        min_price: 0,
        max_price: 0,
        trigger: 'all',
        agent: 'all',
        trigger_date: 'all',
        text: ''
      },
      canCreateAutomatedPrintOrders: true,
      sorting: {},
      total: 0,
      dataOnlyCount: 0,
      activeTotal: 0,
      dataOnlyProperties: [],
      filteredCount: 0,
      isApsHoliday: false,
      newToMarketCount: 0,
      priceReductionCount: 0,
      fallenThroughCount: 0,
      withdrawCount: 0,
      unsoldCount: 0,
      excludedCount: 0,
      blacklistedCount: 0,
      contactedCount: 0,
      prospectingDelay: null,
      nextPrintCollection: null,
      showExcludeModal: false,
      excludeAgentName: null
    }
  },

  computed: {
    ...mapState(['selectedOffice', 'system']),
    propertiesTable () {
      return this.properties
    },
    supportLink () {
      let subject = 'Re-enable Automated Print Request'
      let body = `Please re-enable automated print for our office.

Office: ${this.selectedOffice.name} - ${this.selectedOffice.address_town} (ID: ${this.selectedOffice.id})`
      let url = encodeURI(`${this.system.support_email}?subject=${subject}&body=${body}`)

      return `mailto:${url}`
    },
    salesPriceRange: function () {
      let output = []
      let i = 0

      for (i = 5000; i < 100000; i += 5000) {
        output.push(i)
      }

      for (i = 100000; i < 500000; i += 10000) {
        output.push(i)
      }

      for (i = 500000; i < 1000000; i += 25000) {
        output.push(i)
      }

      for (i = 1000000; i <= 10000000; i += 50000) {
        output.push(i)
      }

      return output
    }
  },

  created () {
    this.getProperties()
  },

  methods: {
    ...mapActions(['displayToastNotification']),
    getProperties (reset = false, page = 1) {
      if (reset) {
        this.getPaginatedProperties(1)
      } else {
        this.getPaginatedProperties(page)
      }
    },
    getPaginatedProperties (page = 1) {
      this.loading = true

      api.get('/aps/manage-data', {
        params: {
          officeID: this.selectedOffice.id,
          trigger: this.filters.trigger,
          department: this.filters.department,
          min_price: this.filters.min_price,
          max_price: this.filters.max_price,
          agent: this.filters.agent,
          trigger_date: this.filters.trigger_date,
          included: this.filters.included,
          data_only: this.filters.data_only,
          text: this.filters.text,
          sorting: JSON.stringify(this.sorting),
          page: page
        }
      }).then(res => {
        this.loading = false
        this.locked = res.data.locked
        this.subscription_count = res.data.subscription_count
        this.properties = res.data.properties
        this.total = res.data.total
        this.activeTotal = res.data.activeTotal
        this.dataOnlyCount = res.data.dataOnlyCount
        this.dataOnlyProperties = res.data.dataOnlyProperties
        this.canCreateAutomatedPrintOrders = res.data.can_create_automated_print_orders
        this.excludedCount = res.data.excludedCount
        this.blacklistedCount = res.data.blacklistedCount
        this.filteredCount = res.data.filteredCount
        this.agentFilter = res.data.agents
        this.triggerDateFilter = res.data.trigger_dates
        this.meta = res.data.meta
        this.isApsHoliday = res.data.is_aps_holiday
        this.newToMarketCount = res.data.new_to_market
        this.priceReductionCount = res.data.price_reduction
        this.fallenThroughCount = res.data.fallen_through
        this.withdrawCount = res.data.withdrawal
        this.unsoldCount = res.data.unsold
        this.contactedCount = res.data.contacted
        this.prospectingDelay = res.data.prospecting_delay
        this.nextPrintCollection = res.data.next_print_collection
      })
    },
    sortBy: function (columnName) {
      if (this.sorting[columnName]) {
        let direction = this.sorting[columnName]
        if (direction === 'asc') {
          // it was sorting asc, so change it to sort desc
          let newSort = {}
          newSort[columnName] = 'desc'
          this.sorting = newSort
        } else {
          // it was sorting desc, don't sort with this field now
          this.sorting[columnName] = ''
          delete this.sorting[columnName]
        }
      } else {
        // it wasn't sorting, sort it asc
        let newSort = {}
        newSort[columnName] = 'asc'
        this.sorting = newSort
      }
      // re-fetch the data for new sorting
      this.getPaginatedProperties()
    },
    changeInclude (property) {
      if (property.include) {
        this.excludedCount--
        api.post('/aps/include-property', {
          officeID: this.selectedOffice.id,
          propertyID: property.id
        })
        return
      }

      this.excludedCount++
      api.post('/aps/exclude-property', {
        officeID: this.selectedOffice.id,
        propertyID: property.id
      })
    },
    deselectAll () {
      api.post('/aps/exclude-all-properties', {
        officeID: this.selectedOffice.id
      }).then(response => {
        this.getProperties()
      })
    },
    includeAll (includeFlag, includeType) {
      // if it's toggling properties on the page, only need to send the property ids
      // if toggling all properties matching filter, need to send request with all filters
      let propertyIds = []

      if (includeType === 'page') {
        propertyIds = this.properties.map(property => property.id)
      }
      let params = {
        officeID: this.selectedOffice.id,
        filters: JSON.stringify(this.filters),
        exclude: !includeFlag,
        propertyIds: JSON.stringify(propertyIds)
      }
      this.loading = true
      api.post('/aps/toggle-exclude-data', params).then(res => {
        if (res.data.message === 'ok') {
          this.getProperties(false, this.meta.current_page)
        } else {
          alert('Your request can not be completed, please try again later or contact the administrator.')
          this.loading = false
        }
      })
    },
    changeBlacklist (property) {
      if (property.blacklist) {
        api.delete(`/aps/blacklist-property/${property.blacklist}`).then(response => {
          property.blacklist = null
          this.blacklistKey++
        })

        return
      }

      api.post('/aps/blacklist-property', {
        office_id: this.selectedOffice.id,
        id: property.id
      }).then(response => {
        if (!response.data.id) {
          return null
        }
        property.blacklist = response.data.id
        this.blacklistKey++
        if (property.include) {
          this.blacklistedCount++
          property.include = false
        }
      })

      this.displayToastNotification({
        type: 'success',
        icon: 'fa-check-circle',
        title: 'Property Blacklisted',
        info: `${property.address}, ${property.postcode}`
      })
    },
    showExcludeAgentModal (agentName) {
      this.excludeAgentName = agentName
      this.showExcludeModal = true
    },
    excludeAgent (agentName) {
      api.post('/aps/exclusions', {
        office_id: this.selectedOffice.id,
        agent_name: agentName
      }).then(res => {
        this.getProperties(true)
        this.excludeAgentName = null
        this.showExcludeModal = false
        this.blacklistKey++
      })
    },
    hideBlacklist () {
      this.getProperties(true)
      this.displayBlacklist = false
    },
    downloadProperty: function (property = null) {
      this.downloadingData = true
      let propertyIds = property ? [property.id] : this.dataOnlyProperties
      api
        .get(`/aps/manage-data/download-property`, {
          params: {
            'property_ids': propertyIds,
            'office_id': this.selectedOffice.id
          },
          responseType: 'blob'
        })
        .then((response) => {
          this.downloadingData = false
          let blob = new Blob([response.data])

          if (!window.navigator.msSaveOrOpenBlob) {
            let url = window.URL.createObjectURL(blob)
            let link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'aps_report.xlsx')
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          } else {
            window.navigator.msSaveOrOpenBlob(blob, 'aps_report.xlsx')
          }
        })
        .catch(() => {
          this.downloadingData = false
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-times-circle',
            title: 'Download Failed',
            info: "We're unable to produce the requested report at this time."
          })
        })
    }
  },

  watch: {
    selectedOffice () {
      this.getProperties()
    }
  }
}
</script>

<style lang="scss" scoped>
.hover-trigger {
  .hover-content {
    position: absolute;
    top: 100%;
    right: 0px;
    width: 200px;
    z-index: 2;
    display: none;
  }

  &:hover {
    .hover-content {
      display: block;
    }
  }
}
</style>
